import { useRef, useState, useEffect, useCallback } from "react";
import Globe from "react-globe.gl";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { fileUrl } from "./Gallery";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GlobeSection = () => {
  const wrapRef = useRef(null);
  const globeRef = useRef(null);
  const [size, setSize] = useState({ w: 300, h: 300 });
  const [trips, setTrips] = useState([]);
  const [active, setActive] = useState(null);
  const [selected, setSelected] = useState(null);
  const [pulse, setPulse] = useState(0);

  useEffect(() => {
    fetch(`${API}/trips`)
      .then((r) => r.json())
      .then((d) => Array.isArray(d) && setTrips(d))
      .catch(() => {});
  }, []);

  // Gentle breathing pulse for pin sizes (oscillates 0 → 1 → 0 …)
  useEffect(() => {
    let frame;
    const start = performance.now();
    const tick = (now) => {
      const t = (now - start) / 1000;
      setPulse((Math.sin(t * 1.6) + 1) / 2); // 0..1
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    const measure = () => {
      if (!wrapRef.current) return;
      const cell = wrapRef.current.clientWidth || 300;
      const dim = Math.max(260, Math.min(cell, window.innerWidth - 48, 600));
      setSize({ w: dim, h: dim });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const onGlobeReady = useCallback(() => {
    const g = globeRef.current;
    if (!g) return;
    g.controls().autoRotate = true;
    g.controls().autoRotateSpeed = 1.1;
    g.controls().enableZoom = false;
    g.controls().enableDamping = true;
    g.controls().dampingFactor = 0.06;
    g.pointOfView({ lat: 30, lng: 15, altitude: 2.4 }, 0);
  }, []);

  const home =
    trips.find((t) => t.is_home) ||
    trips.find((t) => (t.country || "").trim().toLowerCase() === "romania") || {
      // Fallback: Bucharest, Romania
      lat: 44.4268,
      lng: 26.1025,
      country: "Romania",
      is_home: true,
    };
  const arcs = trips
    .filter((t) => t.id !== home.id)
    .map((t) => ({
      startLat: home.lat,
      startLng: home.lng,
      endLat: t.lat,
      endLng: t.lng,
    }));

  const focus = (t) => {
    setActive(t.id);
    globeRef.current?.pointOfView({ lat: t.lat, lng: t.lng, altitude: 1.8 }, 900);
  };

  return (
    <section
      id="calatorii"
      data-testid="globe-section"
      className="relative z-10 py-28 md:py-36 px-6"
    >
      <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1 }}
        >
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D6B56D] mb-6">
            Soul map
          </p>
          <h2 className="font-heading font-light text-5xl md:text-6xl text-[#EBE4D8] leading-tight mb-6">
            Every pin, <br />
            <span className="italic text-[#D6B56D]">a transformation</span>
          </h2>
          <p className="text-[#A59DAB] leading-relaxed max-w-md mb-10">
            Spin the globe and follow the golden trails from home in Romania to
            every place that shaped me. Tap a pin to read its story.
          </p>

          <div className="flex flex-wrap gap-2.5" data-testid="globe-location-list">
            {trips.map((loc) => (
              <button
                key={loc.id}
                data-testid={`globe-loc-${loc.city}`}
                onMouseEnter={() => focus(loc)}
                onClick={() => {
                  focus(loc);
                  setSelected(loc);
                }}
                className={`text-xs tracking-wide px-3.5 py-2 rounded-full border transition-all duration-300 ${
                  active === loc.id
                    ? "border-[#D6B56D] bg-[#D6B56D]/15 text-[#F0D494]"
                    : loc.is_home
                    ? "border-[#F0D494]/60 text-[#F0D494] hover:bg-[#D6B56D]/15"
                    : "border-[#2C223A] text-[#A59DAB] hover:border-[#D6B56D]/60 hover:text-[#EBE4D8]"
                }`}
              >
                {loc.is_home ? "★ " : ""}
                {loc.country}
              </button>
            ))}
          </div>
        </motion.div>

        <div
          ref={wrapRef}
          data-testid="globe-canvas"
          className="relative flex items-center justify-center min-h-[320px] w-full max-w-full overflow-visible"
        >
          {/* Ambient glows behind the globe */}
          <div className="globe-glow-outer" />
          <div className="globe-glow" />

          {/* Two orbiting golden dots */}
          <div
            className="orbit-dot"
            style={{ "--orbit-r": `${size.w * 0.52}px`, animation: "orbit-dot 14s linear infinite" }}
          />
          <div
            className="orbit-dot"
            style={{
              "--orbit-r": `${size.w * 0.56}px`,
              animation: "orbit-dot 22s linear infinite reverse",
              animationDelay: "-7s",
              opacity: 0.7,
            }}
          />

          <div
            className="globe-float-wrap"
            style={{ filter: "drop-shadow(0 0 60px rgba(214,181,109,0.28))" }}
          >
            <Globe
              ref={globeRef}
              width={size.w}
              height={size.h}
              onGlobeReady={onGlobeReady}
              backgroundColor="rgba(0,0,0,0)"
              globeImageUrl="//unpkg.com/three-globe/example/img/earth-night.jpg"
              atmosphereColor="#D6B56D"
              atmosphereAltitude={0.22 + pulse * 0.04}
              pointsData={trips}
              pointLat="lat"
              pointLng="lng"
              pointColor={(d) => (d.is_home ? "#FFFFFF" : "#F0D494")}
              pointAltitude={(d) =>
                (d.is_home ? 0.08 : 0.045) + pulse * (d.is_home ? 0.05 : 0.035)
              }
              pointRadius={(d) =>
                (d.is_home ? 0.85 : 0.5) + pulse * (d.is_home ? 0.35 : 0.25)
              }
              onPointClick={(d) => {
                focus(d);
                setSelected(d);
              }}
              pointLabel={(d) =>
                `<div style="font-family:Outfit;color:#06040A;background:#D6B56D;padding:4px 10px;border-radius:99px;font-size:12px;">${d.country}</div>`
              }
              arcsData={arcs}
              arcColor={() => ["rgba(240,212,148,0.05)", "rgba(240,212,148,1)"]}
              arcDashLength={0.55}
              arcDashGap={0.18}
              arcDashAnimateTime={1800}
              arcStroke={0.55}
              arcAltitudeAutoScale={0.55}
              ringsData={trips}
              ringLat="lat"
              ringLng="lng"
              ringColor={(d) => (t) =>
                d.is_home
                  ? `rgba(255,255,255,${1 - t})`
                  : `rgba(214,181,109,${1 - t})`}
              ringMaxRadius={4.5}
              ringPropagationSpeed={2.4}
              ringRepeatPeriod={900}
            />
          </div>
        </div>
      </div>

      {/* story panel */}
      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="trip-story-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelected(null)}
            className="fixed inset-0 z-[80] flex items-center justify-center p-6 bg-[#05070F]/80 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.94, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, y: 20 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-lg rounded-3xl overflow-hidden border border-[#D6B56D]/25 bg-[#120E1A]"
            >
              <button
                data-testid="trip-story-close"
                onClick={() => setSelected(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-[#06040A]/70 text-[#EBE4D8] flex items-center justify-center hover:bg-[#D6B56D] hover:text-[#06040A] transition-all"
              >
                <X size={18} />
              </button>
              {selected.image_path && (
                <img
                  src={fileUrl(selected.image_path)}
                  alt={selected.city}
                  className="w-full h-60 object-cover"
                />
              )}
              <div className="p-7">
                <p className="text-xs uppercase tracking-[0.25em] text-[#D6B56D] mb-2">
                  {selected.is_home ? "Home" : "A place that shaped me"}
                </p>
                <h3 className="font-heading text-3xl text-[#EBE4D8] mb-4">
                  <span className="italic text-[#D6B56D]">{selected.country}</span>
                </h3>
                <p className="text-[#A59DAB] leading-relaxed font-light">
                  {selected.story || "A chapter still waiting to be written."}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
